// split-test: resource_show_modal_view

/* eslint-disable no-undef */
import EzModalController from 'layouts/modal/modal-controller';
import { fetchState, setState } from 'lib/state-helpers';
import { insertAjaxContent, getAjaxModal } from 'lib/ajax-helpers';
import { triggerCustomEvent, getKeyByValue } from 'lib/helpers';
import { initTooltips } from 'ez-styles/assets/javascripts/ez-styles/utils/tooltips';
import events from 'lib/events';

export default class ResourceShowModalViewController extends EzModalController {
  static targets = [
    'adTemplate',
    'content',
    'navLinkNext',
    'navLinkPrev',
  ];

  connect() {
    super.connect();

    this.modalViewInit();
  }

  modalViewInit() {
    window.disableDownloadLimitModal = false;

    this.contentTarget.classList.remove('is-hidden');

    this.setupPageState();

    this.getAsyncContent();

    this.initAds();

    window.addEventListener(events.show_page.modal.history_close_modal_view, this.historyClose);
    window.addEventListener(events.show_page.modal.reload_modal_view, this.reloadModalView);
    window.addEventListener(events.show_page.modal.update_resource_navigation_context, this.updateNavContext);

    this.setupNavPos();

    this.displayNavElements();
  }

  setupPageState() {
    this.modalTitle = decodeURI(this.element.getAttribute('data-modal-title'));
    this.modalPath = this.element.getAttribute('data-modal-path');
    this.referringTitle = document.body.getAttribute('data-referring-title');
    this.referringPath = document.body.getAttribute('data-referring-path');

    if (this.element.querySelectorAll('.ez-tooltip-icon').length > 0) {
      initTooltips();
    }

    if ('turbolinks' in window.history) {
      this.previousStateObj = window.history.state;
    }

    if (!this.element.getAttribute('data-from-history')) {
      window.history.pushState({
        modalData: {
          id: 'resource-show-modal-view',
          title: this.modalTitle,
          path: this.modalPath,
          contextPath: this.referringPath,
          navContext: fetchState('resources-nav-context'),
        },
      }, this.modalTitle, this.modalPath);
    }

    // This must come *after* history change to make sure titles in history are correct
    document.title = this.modalTitle;

    triggerCustomEvent(events.analytics.fire_page_view);
  }

  getAsyncContent() {
    const resourceId = this.element.querySelector('[data-controller*="resource-show-main"]').getAttribute('data-resource-id');

    insertAjaxContent(`/resources/${resourceId}/show_modal_async_content`,
      this.contentTarget,
      'beforeend',
      () => {
        this.initAds();
      });
  }

  setupNavPos() {
    this.contextState = window.history.state.navContext || fetchState('resources-nav-context');

    const curPos = parseInt(this.contextState.currentPosition, 10);

    this.currentResourcePath = this.contextState[curPos];

    this.prevResourcePath = this.contextState[curPos - 1];

    this.nextResourcePath = this.contextState[curPos + 1];
  }

  displayNavElements() {
    if (this.prevResourcePath) {
      this.navLinkPrevTargets.forEach((el) => {
        el.classList.remove('is-disabled');
      });
      window.addEventListener('keyup', this.keyboardNavLeft);
    }

    if (this.nextResourcePath) {
      this.navLinkNextTargets.forEach((el) => {
        el.classList.remove('is-disabled');
      });
      window.addEventListener('keyup', this.keyboardNavRight);
    }
  }

  keyboardNavLeft = (event) => {
    if (event.keyCode === 37) {
      this.navLinkPrevTarget.classList.add('is-active');
      this.navLinkPrevTarget.click();
    }
  }

  keyboardNavRight = (event) => {
    if (event.keyCode === 39) {
      this.navLinkNextTarget.classList.add('is-active');
      this.navLinkNextTarget.click();
    }
  }

  navigate = (event) => {
    event.preventDefault();

    const navDirection = event.currentTarget.getAttribute('data-direction');

    const targetResourcePath = this[`${navDirection}ResourcePath`];

    triggerCustomEvent(events.show_page.modal.update_resource_navigation_context,
      { newCurrentPath: targetResourcePath });

    triggerCustomEvent(events.show_page.modal.reload_modal_view);

    triggerCustomEvent(events.show_page.modal[`${navDirection}_arrow_click`], {}); // split-test: resource_show_modal_view

    getAjaxModal(targetResourcePath);
  }

  updateNavContext = (event) => {
    const { newCurrentPath } = event.detail;

    this.contextState.currentPosition = getKeyByValue(this.contextState, newCurrentPath);

    setState('resources-nav-context', this.contextState);
  }

  initAds() {
    if (document.querySelector('[data-controller="ads-main"]') && typeof TMSAds !== 'undefined') {
      const adTemplates = this.adTemplateTargets;

      // Save the current page's layout
      this.legacyAdLayout = document.querySelector('[data-controller="ads-main"]').getAttribute('data-layout');

      // Now set the layout to the show page
      TMSAds.setLayout('show');

      adTemplates.forEach((template) => {
        const thisAdWrap = template.closest('.ez-ad-container');
        const thisAdHtml = template.innerHTML;

        thisAdWrap.insertAdjacentHTML('beforeend', thisAdHtml);
      });
    }
  }

  reloadModalView = () => {
    this.contentTarget.classList.add('is-hidden');

    this.addLoadingOverlay();

    this.cleanupEventListeners();
  }

  addLoadingOverlay() {
    const modalWrap = this.element.querySelector('.ez-modal__modal');
    this.loadingOverlayHtml = '<div class="resource-show-modal-view__loading-overlay" data-target="resource-show-modal-view.loadingOverlay"><div class="ez-spinner ez-spinner"></div></div>';

    modalWrap.insertAdjacentHTML(
      'afterBegin',
      this.loadingOverlayHtml,
    );
  }

  close = (event = null) => {
    super.close(event, this.beforeClose);
  }

  historyClose = (event) => {
    triggerCustomEvent(events.modal.close_modal, { historyClose: true, ...event.detail });
  }

  beforeClose = (event) => {
    const eventDetail = (event && (typeof event.detail !== 'undefined')) ? event.detail : {};

    const updateState = typeof eventDetail.updateState !== 'undefined' ? eventDetail.updateState : true;

    const historyClose = eventDetail.historyClose || false;

    // split-test: resource_show_modal_view
    if (!historyClose
        && (event && event.currentTarget.classList
         && event.currentTarget.classList.contains('ez-close-btn'))) {
      triggerCustomEvent(events.show_page.modal.close_click);
    }

    this.cleanup(historyClose, updateState);
  }

  cleanup(historyClose, updateState) {
    this.cleanupEventListeners();

    if (updateState) {
      const newState = this.previousStateObj || {};

      if (historyClose) {
        window.history.replaceState(newState, this.referringTitle, this.referringPath);
      } else {
        window.history.pushState(newState, this.referringTitle, this.referringPath);
      }
    }

    // This must come *after* history change to make sure titles in history are correct
    document.title = this.referringTitle;

    if (document.querySelector('[data-controller="ads-main"]') && typeof TMSAds !== 'undefined') {
      // Set the ad layout back to original deal
      TMSAds.setLayout(this.legacyAdLayout);
    }
  }

  cleanupEventListeners() {
    window.removeEventListener(events.show_page.modal.history_close_modal_view, this.historyClose);
    window.removeEventListener(events.show_page.modal.reload_modal_view, this.reloadModalView);
    window.removeEventListener(events.show_page.modal.update_resource_navigation_context, this.updateNavContext);
    window.removeEventListener('keyup', this.keyboardNavLeft);
    window.removeEventListener('keyup', this.keyboardNavRight);
  }
}
/* eslint-enable no-undef */
